import { resolveForRole } from '../../lib/roleHistory';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Star,
  Target,
  Clock,
  CheckCircle2,
  ArrowRight,
  MapPin,
  Eye,
  EyeOff,
  Lock,
  Users,
  Trophy,
  Sparkles,
  MessageCircle,
  LogIn,
  UserPlus,
  KeyRound,
  Loader2,
  AlertCircle,
  Repeat,
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import StructuredData, { 
  getOrganizationSchema, 
  getLocalBusinessSchema,
  getFAQSchema 
} from '../../components/StructuredData';
import { formatDateInTimezone } from '../../utils/timezone';
import SplashAnnouncement from '../../components/SplashAnnouncement';
import { HeroFeatureCards, HeroCta, shownItems, DEFAULT_HERO_CARDS } from './landing/HeroParts';
import { buildHandoffUrl, isPreviewHost, normHost } from '../../utils/crossHostAuth';

const API_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL);

const DAYS_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

const _FOCUS_AREAS = [
  { value: 'batting', label: 'Batting' },
  { value: 'bowling', label: 'Bowling' },
  { value: 'fielding', label: 'Fielding' },
  { value: 'wicket_keeping', label: 'Wicket Keeping' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'match_play', label: 'Match Play' }
];

const SKILL_LEVELS = [
  { value: 'beginner', label: 'Beginner - New to cricket' },
  { value: 'intermediate', label: 'Intermediate - Some experience' },
  { value: 'advanced', label: 'Advanced - Competitive player' }
];

const DEFAULT_MENU_ITEMS = [
  { label: 'Programs', link: '#programs' },
  { label: 'Coaching', link: '#coaching' },
  { label: 'Pathway', link: '#player-pathway' },
  { label: 'Pricing', link: '#pricing' },
  { label: 'Coaches', link: '#our-coaches' },
  { label: 'Contact', link: '#contact' },
  { label: 'Our Players', link: '/our-players', highlight: true },
  { label: 'Book Lane', link: '/book-lane', highlight: true },
  { label: 'Register for Camp', link: '/camps/register', highlight: true },
  { label: 'News', link: '/blog', highlight: true },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [_searchParams] = useSearchParams();
  const { login, setAuth, user: authUser, loading: authLoading } = useAuth();
  // iter308e (Phase 5b.1): consume tenant branding instead of
  // hardcoding "Delhi Capitals Cricket Academy NJ" everywhere. As
  // new tenants come online, each host loads its own branding via
  // /api/public/branding and this component renders the right name.
  const { branding } = useTenant();
  const tenantDisplayName = branding?.business_name || 'Cricket Academy';
  
  // Get initial tab from URL on first render
  const getInitialTab = () => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    // Persist the intended post-auth redirect, if present, so that after
    // login/signup the existing redirect-after-login logic takes the user
    // to their booking page (e.g. /book for customers).
    const redirectParam = params.get('redirect');
    if (redirectParam) {
      try {
        localStorage.setItem('redirectAfterLogin', redirectParam);
      } catch {
        // ignore storage errors
      }
    }
    if (tab === 'login') return 'login';
    if (tab === 'register' || tab === 'signup') return 'register';
    return 'trial';
  };
  
  // Tab state - check URL params on initial load
  const [activeTab, setActiveTab] = useState(getInitialTab);
  
  // Trial form state
  const [formData, setFormData] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_name: '',
    date_of_birth: '',
    skill_level: 'beginner',
    cricket_experience: '',
    preferred_days: [],
    focus_areas: [],
    additional_notes: '',
    create_account: false,
    password: '',
    confirm_password: ''
  });
  
  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(true);
  
  // Register form state
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    role: 'parent'
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [playerError, setPlayerError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);

  // Admin-editable homepage sections — fetched once and consulted by
  // every section block to honor `enabled` / `order` / per-field copy.
  // Falls back to an empty `byId` map when the request fails so that
  // the hardcoded defaults below still render.
  const [homepageSections, setHomepageSections] = useState([]);
  const sectionsById = homepageSections.reduce((acc, s) => {
    acc[s.id] = s;
    return acc;
  }, {});
  // Helper: returns the section's `content` blob iff the section is
  // enabled, otherwise returns null. Calling sites use:
  //   const sec = getSectionContent('hero'); if (!sec) return null;
  // Note: NOT prefixed with `use` because that triggers React's
  // rules-of-hooks lint when called inside the IIFE wrappers.
  const getSectionContent = (id) => sectionsById[id]?.content || null;
  // `sectionEnabled` only returns true when the fetched config explicitly
  // contains an enabled section with that id. If the fetch hasn't completed
  // yet (`homepageSections.length === 0`) we render the hardcoded fallback
  // so the page never appears empty during initial paint or fetch failure.
  // Once the fetch succeeds, an admin-disabled section is filtered out by
  // the backend → absent from `sectionsById` → returns false → hidden.
  const sectionEnabled = (id) =>
    homepageSections.length === 0
      ? true
      : !!sectionsById[id] && sectionsById[id].enabled !== false;
  
  // Banner announcements state
  const [bannerAnnouncements, setBannerAnnouncements] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  
  // Mobile login modal state
  const [showMobileLoginModal, setShowMobileLoginModal] = useState(false);
  
  // Get Started dropdown state
  const [_showGetStartedMenu, setShowGetStartedMenu] = useState(false);
  
  // Auth box state (for collapsible inline form - keeping for backward compatibility)
  const [_authBoxExpanded, _setAuthBoxExpanded] = useState(true);
  
  // Auth modal state (new floating dropdown approach)
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  // Hero images state
  const [heroImages, setHeroImages] = useState([]);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [_instagramSettings, setInstagramSettings] = useState(null);
  
  // Business logo state - initialize from localStorage to prevent flash
  const [businessLogo, setBusinessLogo] = useState(() => {
    return localStorage.getItem('businessLogoUrl') || null;
  });
  const [logoSize, setLogoSize] = useState(() => {
    return localStorage.getItem('businessLogoSize') || 'medium';
  });
  const [logoAlignment, setLogoAlignment] = useState(() => {
    return localStorage.getItem('businessLogoAlignment') || 'left';
  });
  
  // Logo size mapping to CSS classes
  const logoSizeClasses = {
    small: { header: 'h-10', nav: 'h-8' },
    medium: { header: 'h-14', nav: 'h-10' },
    large: { header: 'h-20', nav: 'h-14' },
    xlarge: { header: 'h-24', nav: 'h-16' }
  };
  
  // Logo alignment mapping
  const _logoAlignmentClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end'
  };
  
  // Ensure localStorage values are applied after hydration
  useEffect(() => {
    const storedAlignment = localStorage.getItem('businessLogoAlignment');
    const storedSize = localStorage.getItem('businessLogoSize');
    const storedLogo = localStorage.getItem('businessLogoUrl');
    
    if (storedAlignment && storedAlignment !== logoAlignment) {
      setLogoAlignment(storedAlignment);
    }
    if (storedSize && storedSize !== logoSize) {
      setLogoSize(storedSize);
    }
    if (storedLogo && storedLogo !== businessLogo) {
      setBusinessLogo(storedLogo);
    }
  }, []);

  // If the user is already authenticated, send them to their role's home
  // (otherwise the public landing page appears as if they were logged out
  // after navigating back from public sub-pages like /programs or /blog).
  // Respect an explicit `?force=1` param so admins can still view the
  // landing page for editing/demoing purposes.
  useEffect(() => {
    if (authLoading) return;
    if (!authUser) return;
    const force = new URLSearchParams(window.location.search).get('force');
    if (force === '1') return;
    const roleHome = {
      super_admin: '/super-admin/tenants',
      admin: '/admin',
      staff: '/staff',
      coach: '/coach',
      parent: '/parent',
      customer: '/book',
      player: '/player'
    }[authUser.role] || '/';
    navigate(roleHome, { replace: true });
  }, [authUser, authLoading, navigate]);
  
  // Homepage events state
  const [homepageEvents, setHomepageEvents] = useState([]);
  const [businessTimezone, setBusinessTimezone] = useState('America/New_York');
  
  // Sticky nav state
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Player registration state
  const [playerData, setPlayerData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    date_of_birth: '',
    gender: '',
    skill_level: 'beginner',
    parent_name: '',
    parent_email: '',
    parent_phone: ''
  });
  const [playerLoading, setPlayerLoading] = useState(false);
  const [playerAge, setPlayerAge] = useState(null);
  const [claimMode, setClaimMode] = useState(false);
  const [claimCheckResult, setClaimCheckResult] = useState(null);
  // 2-step claim flow (CVE-5): null = collect email+password,
  // 'verifying' = collect 6-digit code from email.
  const [claimVerifyStep, setClaimVerifyStep] = useState(null);
  const [claimVerifyCode, setClaimVerifyCode] = useState('');

  // Maintenance mode state
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  // Check maintenance status directly from API
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await api.get('/maintenance-status');
        // Store maintenance status - admin check will be done on the backend during login
        setMaintenanceMode(response.data.maintenance_mode);
        setMaintenanceMessage(response.data.maintenance_message || 'We are currently performing scheduled maintenance. Please check back shortly.');
      } catch (error) {
        // If we can't reach the API, don't block access
        setMaintenanceMode(false);
      }
    };
    
    checkMaintenance();
    // Re-check periodically
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch contact info on mount
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/contact-info`);
        if (response.ok) {
          const data = await response.json();
          setContactInfo(data);
        }
      } catch (error) {
        console.error('Error fetching contact info:', error);
      }
    };
    fetchContactInfo();
  }, []);

  // Pull the admin-editable homepage layout. Best-effort; on failure
  // we leave `homepageSections` empty and the hardcoded defaults
  // below render unchanged so the public page never appears broken.
  useEffect(() => {
    const fetchHomepage = async () => {
      try {
        const r = await fetch(`${API_URL}/api/homepage-config/public`);
        if (r.ok) {
          const data = await r.json();
          setHomepageSections(data.sections || []);
        }
      } catch (e) {
        console.error('Error fetching homepage config:', e);
      }
    };
    fetchHomepage();
  }, []);

  // Fetch banner announcements
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch(`${API_URL}/api/splash/banners/active`);
        if (response.ok) {
          const data = await response.json();
          setBannerAnnouncements(data || []);
        }
      } catch (error) {
        console.error('Error fetching banner announcements:', error);
      }
    };
    fetchBanners();
  }, []);

  // Auto-rotate banner announcements
  useEffect(() => {
    if (bannerAnnouncements.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentBannerIndex(prev => (prev + 1) % bannerAnnouncements.length);
    }, 5000); // Rotate every 5 seconds
    
    return () => clearInterval(interval);
  }, [bannerAnnouncements.length]);

  // Scroll listener for sticky nav
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch hero images and instagram settings
  useEffect(() => {
    const fetchHeroImages = async () => {
      try {
        const [imagesRes, instaRes, eventsRes, tzRes, logoRes] = await Promise.all([
          fetch(`${API_URL}/api/hero-images?active_only=true`),
          fetch(`${API_URL}/api/hero-images/instagram-feed`),
          fetch(`${API_URL}/api/events/homepage`),
          fetch(`${API_URL}/api/admin/timezone-settings/public`),
          fetch(`${API_URL}/api/admin/logo`)
        ]);
        if (imagesRes.ok) {
          const images = await imagesRes.json();
          setHeroImages(images || []);
        }
        if (instaRes.ok) {
          const insta = await instaRes.json();
          setInstagramSettings(insta);
        }
        if (eventsRes.ok) {
          const events = await eventsRes.json();
          setHomepageEvents(events || []);
        }
        if (tzRes.ok) {
          const tzData = await tzRes.json();
          setBusinessTimezone(tzData.timezone || 'America/New_York');
        }
        if (logoRes.ok) {
          const logoData = await logoRes.json();
          if (logoData.logo_url) {
            setBusinessLogo(logoData.logo_url);
            localStorage.setItem('businessLogoUrl', logoData.logo_url);
          } else {
            // No custom logo - clear cache
            setBusinessLogo(null);
            localStorage.removeItem('businessLogoUrl');
          }
          // Update logo size
          if (logoData.logo_size) {
            setLogoSize(logoData.logo_size);
            localStorage.setItem('businessLogoSize', logoData.logo_size);
          }
          // Update logo alignment
          if (logoData.logo_alignment) {
            setLogoAlignment(logoData.logo_alignment);
            localStorage.setItem('businessLogoAlignment', logoData.logo_alignment);
          }
        }
      } catch (error) {
        console.error('Error fetching hero images:', error);
      }
    };
    fetchHeroImages();
  }, []);

  // Auto-advance hero image carousel
  useEffect(() => {
    if (heroImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [heroImages.length]);

  // Smooth scroll to section
  // Top menu bar — editable in Homepage Editor → Header (eye icon hides items).
  const menuItems = (sectionsById.header?.content?.menu_items || DEFAULT_MENU_ITEMS).filter((m) => m && !m.hidden && m.label);
  const goMenu = (link) => {
    const l = String(link || '');
    if (l.startsWith('#')) scrollToSection(l.slice(1));
    else if (/^https?:\/\//.test(l)) window.location.href = l;
    else navigate(l || '/');
  };
  // Hero (Homepage Editor → Hero): feature cards, photo panel, button + quick links.
  const heroContent = getSectionContent('hero') || {};
  const heroCards = shownItems(heroContent.feature_cards, DEFAULT_HERO_CARDS);
  const heroPanel = heroContent.show_media_panel !== false;
  const heroAction = (action, url) => {
    if (action === 'url') { goMenu(url); return; }
    if (action === 'signin') setActiveTab('login');
    else if (action === 'trial') setActiveTab('trial');
    else if (action === 'signup') setActiveTab('register');
    setShowAuthModal(true);
  };
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
    setShowGetStartedMenu(false);
  };

  // Open Get Started menu and scroll to form
  const _openGetStartedForm = (tab) => {
    setActiveTab(tab);
    setShowGetStartedMenu(false);
    // Scroll to form section
    setTimeout(() => {
      const formSection = document.getElementById('get-started-forms');
      if (formSection) {
        formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Expand auth box and switch to specified tab
  const expandAuthBox = (tab) => {
    setActiveTab(tab);
    setShowAuthModal(true);
  };

  // Format phone for display
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+1 ${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Format phone for WhatsApp link
  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/[^0-9+]/g, '');
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }
    return cleaned;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleDay = (day) => {
    setFormData(prev => {
      const isSelected = prev.preferred_days.includes(day);
      // Allow deselection, but limit selection to max 3 days
      if (!isSelected && prev.preferred_days.length >= 3) {
        toast.error('Please select up to 3 preferred days');
        return prev;
      }
      return {
        ...prev,
        preferred_days: isSelected
          ? prev.preferred_days.filter(d => d !== day)
          : [...prev.preferred_days, day]
      };
    });
  };

  const _toggleFocusArea = (area) => {
    setFormData(prev => ({
      ...prev,
      focus_areas: prev.focus_areas.includes(area)
        ? prev.focus_areas.filter(a => a !== area)
        : [...prev.focus_areas, area]
    }));
  };

  // Handle trial form submission
  const handleTrialSubmit = async (e) => {
    e.preventDefault();

    if (formData.create_account) {
      if (!formData.password || formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (formData.password !== formData.confirm_password) {
        toast.error('Passwords do not match');
        return;
      }
    }

    setLoading(true);

    try {
      const payload = {
        parent_name: formData.parent_name,
        parent_email: formData.parent_email,
        parent_phone: formData.parent_phone,
        child_name: formData.child_name,
        date_of_birth: formData.date_of_birth,
        skill_level: formData.skill_level,
        cricket_experience: formData.cricket_experience || null,
        preferred_days: formData.preferred_days,
        focus_areas: formData.focus_areas,
        additional_notes: formData.additional_notes || null,
        create_account: formData.create_account,
        password: formData.create_account ? formData.password : null
      };

      const response = await api.post('/trial-requests/public', payload);
      setResult(response.data);
      setSubmitted(true);
      toast.success('Trial class request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (!loginData.email || !loginData.password) {
      setLoginError('Please enter both email and password');
      toast.error('Please enter both email and password');
      return;
    }
    
    setLoginLoading(true);

    try {
      // Use the AuthContext login function which handles the API call
      const user = await login(loginData.email, loginData.password, rememberMe);
      toast.success('Login successful!');

      // iter312 — Cross-host redirect for tenant users who land on the
      // wrong host (e.g. parents who get a magic link to my.vyomos.io
      // and submit credentials there). Token handoff via URL fragment
      // preserves the session across the origin boundary.
      if (user?.role && user.role !== 'super_admin' && !isPreviewHost()) {
        try {
          const meRes = await fetch('/api/tenant/me', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          if (meRes.ok) {
            const me = await meRes.json();
            const targetHost = me.canonical_hostname || me.custom_domain || me.subdomain_host;
            if (
              targetHost &&
              normHost(targetHost) &&
              normHost(targetHost) !== normHost(window.location.host)
            ) {
              const roleRoutes = { admin: '/admin', staff: '/staff', coach: '/coach', parent: '/parent', customer: '/book', player: '/player' };
              const path = roleRoutes[user.role] || '/';
              window.location.replace(buildHandoffUrl(targetHost, path));
              return;
            }
          }
        } catch (_e) {
          // network blip — fall through to local navigation
        }
      }

      // Check for saved redirect URL (from ProtectedRoute when accessing protected pages)
      const savedRedirect = localStorage.getItem('redirectAfterLogin');
      // Role-less deep links resolve to the signed-in user's own role tree.
      const redirectUrl = savedRedirect ? resolveForRole(savedRedirect, user.role) : null;
      if (redirectUrl) {
        // For parent users, check if the URL is a parent route
        const isParentRoute = redirectUrl.startsWith('/parent');
        if ((user.role === 'parent' && isParentRoute) || redirectUrl.startsWith(`/${user.role}`)) {
          localStorage.removeItem('redirectAfterLogin');
          navigate(redirectUrl);
          return;
        }
      }
      localStorage.removeItem('redirectAfterLogin');
      
      // Redirect based on role
      const roleRoutes = {
        admin: '/admin',
        staff: '/staff',
        coach: '/coach',
        parent: '/parent',
        customer: '/book',
        player: '/player'
      };
      navigate(roleRoutes[user.role] || '/');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      const statusCode = error.response?.status;
      
      // Handle maintenance mode error (503)
      if (statusCode === 503) {
        setMaintenanceMode(true);
        setMaintenanceMessage(errorMessage);
        setLoginError(errorMessage);
        toast.error(errorMessage, {
          duration: 6000,
          description: 'Please try again later or contact an administrator.'
        });
      } else if (statusCode === 423) {
        // iter318 wave 9 (iter281 fix) — Account lockout. Backend
        // detail is generic ("Too many failed sign-in attempts…") so
        // we build a friendlier client-side message that mentions
        // the lock explicitly and points to next steps.
        const lockMsg = 'Account temporarily locked after too many wrong-password attempts. Please try again in about 15 minutes or contact an administrator.';
        setLoginError(lockMsg);
        toast.error(lockMsg, {
          duration: 8000,
          description: 'For quicker access, ask your admin to unlock the account from the Users page.'
        });
      } else if (statusCode === 429) {
        const rlMsg = 'Too many login attempts from this device. Please wait a minute and try again.';
        setLoginError(rlMsg);
        toast.error(rlMsg, {
          duration: 6000,
          description: 'This is a temporary rate limit to protect your account.'
        });
      } else if (errorMessage.toLowerCase().includes('invalid credentials')) {
        const friendlyMessage = 'Invalid email or password. Please check your credentials and try again.';
        setLoginError(friendlyMessage);
        toast.error(friendlyMessage, {
          duration: 5000,
          description: 'If you forgot your password, use the "Forgot Password" link below.'
        });
      } else {
        setLoginError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle forgot password submission
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotPasswordLoading(true);

    try {
      await api.post('/auth/forgot-password', { 
        email: forgotPasswordEmail,
      });
      setForgotPasswordSent(true);
      toast.success('Password reset instructions sent to your email');
    } catch (error) {
      // Still show success message to prevent email enumeration
      setForgotPasswordSent(true);
      toast.success('If an account exists with this email, you will receive a reset link.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Calculate player age from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Handle player DOB change
  const handlePlayerDobChange = (e) => {
    const dob = e.target.value;
    setPlayerData({ ...playerData, date_of_birth: dob });
    setPlayerAge(calculateAge(dob));
  };

  // Check if player email exists (for claim flow)
  const checkPlayerEmail = async () => {
    if (!playerData.email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setPlayerLoading(true);
    try {
      const response = await api.get(`/player/check-email/${playerData.email}`);
      setClaimCheckResult(response.data);
      
      if (response.data.exists && !response.data.has_account) {
        // Player found, can claim
        setClaimMode(true);
        setPlayerError('');
        toast.success(`Found player: ${response.data.player_name}. You can claim this account.`);
      } else if (response.data.has_account) {
        // Account already exists
        setClaimMode(false);
        setPlayerError('');
        toast.info('An account already exists. Please sign in.');
        setActiveTab('login');
        setLoginData({ ...loginData, email: playerData.email });
      } else {
        // Email not found in system
        setClaimMode(false);
        setClaimCheckResult(null);
        const errorMsg = 'No player found with this email. Please check your email or contact the academy or your parent if you believe this is an error.';
        setPlayerError(errorMsg);
        toast.error(errorMsg, { duration: 6000 });
      }
    } catch (error) {
      setClaimCheckResult(null);
      setClaimMode(false);
      const errorMsg = error.response?.data?.detail || 'Unable to check email. Please try again.';
      setPlayerError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setPlayerLoading(false);
    }
  };

  // Handle player registration/claim
  const handlePlayerRegister = async (e) => {
    e.preventDefault();
    
    if (!playerData.email) {
      toast.error('Email is required');
      return;
    }
    if (!playerData.password || playerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (playerData.password !== playerData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    // For claim mode, 2-step verification flow (CVE-5):
    //   Step 1 (no code) → backend emails a 6-digit code, we show the code input.
    //   Step 2 (with code) → backend creates the account and returns the JWT.
    if (claimMode) {
      setPlayerLoading(true);
      try {
        if (claimVerifyStep !== 'verifying') {
          // Step 1: request code
          const response = await api.post('/player/claim-account', {
            email: playerData.email,
            password: playerData.password,
          });
          if (response.data?.requires_verification) {
            setClaimVerifyStep('verifying');
            toast.success(response.data.message || 'A verification code was sent to your email.');
          } else if (response.data?.access_token) {
            // Backwards compat in case backend doesn't require verification
            localStorage.setItem('token', response.data.access_token);
            toast.success('Account claimed successfully!');
            setAuth(response.data.access_token, response.data.user);
            navigate('/player');
          }
        } else {
          // Step 2: verify code
          if (!claimVerifyCode || claimVerifyCode.length < 4) {
            toast.error('Please enter the 6-digit code sent to your email');
            setPlayerLoading(false);
            return;
          }
          const response = await api.post('/player/claim-account', {
            email: playerData.email,
            verification_code: claimVerifyCode,
          });
          localStorage.setItem('token', response.data.access_token);
          toast.success('Account claimed successfully!');
          setAuth(response.data.access_token, response.data.user);
          navigate('/player');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to claim account');
      } finally {
        setPlayerLoading(false);
      }
      return;
    }
    
    // New registration validation
    if (!playerData.name) {
      toast.error('Name is required');
      return;
    }
    if (!playerData.date_of_birth) {
      toast.error('Date of birth is required');
      return;
    }
    
    const age = calculateAge(playerData.date_of_birth);
    if (age < 18) {
      if (!playerData.parent_name) {
        toast.error('Parent name is required for players under 18');
        return;
      }
      if (!playerData.parent_email) {
        toast.error('Parent email is required for players under 18');
        return;
      }
    }
    
    setPlayerLoading(true);
    
    try {
      const response = await api.post('/player/register', playerData);
      localStorage.setItem('token', response.data.access_token);
      toast.success('Player account created successfully!');
      setAuth(response.data.access_token, response.data.user);
      navigate('/player');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setPlayerLoading(false);
    }
  };

  // Handle registration submission
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.password || registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (registerData.password !== registerData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    setRegisterLoading(true);

    try {
      const payload = {
        name: registerData.name,
        email: registerData.email,
        password: registerData.password,
        phone: registerData.phone || null,
        role: registerData.role
      };
      
      const response = await api.post('/auth/register', payload);
      const { access_token, user } = response.data;
      
      // Store token and redirect
      localStorage.setItem('token', access_token);
      toast.success('Registration successful!');
      
      // Redirect based on role
      const roleRoutes = {
        parent: '/parent',
        customer: '/book'
      };
      navigate(roleRoutes[user.role] || '/');
      
      // Reload to update auth context
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Email may already be registered.');
    } finally {
      setRegisterLoading(false);
    }
  };

  // Success State after trial submission
  if (submitted && result) {
    return (
      <div className="min-h-screen bg-slate-900 cricket-pattern flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        
        <div className="w-full max-w-md animate-slideUp relative z-10">
          <Card className="bg-white/95 backdrop-blur shadow-2xl border-0">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="font-heading text-2xl text-emerald-700">
                Request Submitted!
              </CardTitle>
              <CardDescription className="text-base">
                Thank you for your interest in {tenantDisplayName}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-800">Child's Name:</span> {result.child_name}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-800">Request ID:</span> {result.request_id?.slice(0, 8)}...
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium text-slate-800">Status:</span>{' '}
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                    Pending Review
                  </span>
                </p>
              </div>

              {result.account_created && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-800 mb-1">Account Created!</p>
                  <p className="text-sm text-blue-700">
                    You can now log in with <span className="font-mono">{result.login_email}</span> and the password you created.
                  </p>
                </div>
              )}

              {result.existing_account && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-800 mb-1">Account Already Exists</p>
                  <p className="text-sm text-amber-700">
                    You can log in with <span className="font-mono">{result.login_email}</span> to check your trial request status.
                  </p>
                </div>
              )}

              <div className="text-center text-sm text-slate-600">
                <p>Our team will review your request and contact you within 24-48 hours to schedule a trial class.</p>
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <Button 
                  className="w-full bg-slate-900 hover:bg-slate-800" 
                  onClick={() => {
                    setSubmitted(false);
                    setActiveTab('login');
                  }}
                  data-testid="goto-login-btn"
                >
                  Go to Login
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setSubmitted(false);
                    setResult(null);
                    setFormData({
                      parent_name: '',
                      parent_email: '',
                      parent_phone: '',
                      child_name: '',
                      date_of_birth: '',
                      skill_level: 'beginner',
                      cricket_experience: '',
                      preferred_days: [],
                      focus_areas: [],
                      additional_notes: '',
                      create_account: false,
                      password: '',
                      confirm_password: ''
                    });
                  }}
                  data-testid="submit-another-btn"
                >
                  Submit Another Request
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // FAQ data for structured data
  const faqData = [
    {
      question: "What age groups do you offer cricket training for?",
      answer: `${tenantDisplayName} offers cricket training for all ages, from young beginners (6+) to adult players. We have age-appropriate programs including U9, U11, U13, U15, and adult categories.`
    },
    {
      question: "Do I need prior cricket experience to join?",
      answer: "No prior experience is needed! We welcome complete beginners as well as experienced players. Our coaches assess each player's skill level and provide personalized training."
    },
    {
      question: "Can I try a class before enrolling?",
      answer: "Yes! We offer trial classes so you can experience our coaching style and facilities before committing to a program. Register for a free trial on our website."
    },
    {
      question: "What are your training hours?",
      answer: "We're open Monday-Friday 4pm-10pm and Saturday-Sunday 7am-9pm. Classes are scheduled throughout these hours based on age groups and programs."
    },
    {
      question: "What payment plans do you offer?",
      answer: "We offer flexible payment options including monthly, quarterly, and bi-annual plans. Quarterly and bi-annual plans come with discounts."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 cricket-pattern relative" data-themed="true">
      {/* Splash Announcement */}
      <SplashAnnouncement />
      
      {/* SEO Structured Data */}
      <StructuredData schema={[
        getOrganizationSchema(contactInfo || {}, tenantDisplayName),
        getLocalBusinessSchema(tenantDisplayName, contactInfo || {}),
        getFAQSchema(faqData)
      ]} />
      
      {/* Upcoming Event Banner - Displayed at very top when there are homepage events */}
      {homepageEvents.length > 0 && (
        <div className="relative z-50 bg-gradient-to-r from-emerald-600 to-blue-600 py-2 px-4">
          <div className="max-w-6xl mx-auto space-y-2">
            {homepageEvents.map((event, index) => (
              <div key={event.id} className={`flex items-center justify-between gap-4 flex-wrap ${index > 0 ? 'pt-2 border-t border-white/20' : ''}`}>
                <div className="flex items-center gap-3 text-white">
                  <span className="hidden sm:inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/20 flex-shrink-0">
                    <Calendar className="w-4 h-4" />
                  </span>
                  <div>
                    <span className="font-medium">{event.title}</span>
                    <span className="text-emerald-200 ml-2 text-sm">
                      {formatDateInTimezone(event.start_datetime, businessTimezone, { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </div>
                <a 
                  href={`/events/rsvp/${event.public_rsvp_token}`}
                  className="inline-flex items-center gap-2 px-4 py-1.5 bg-white text-emerald-700 rounded-full text-sm font-medium hover:bg-emerald-50 transition-colors"
                >
                  RSVP Now
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 py-4 px-4 border-b border-white/10">
        <div className="max-w-6xl mx-auto">
          {/* Top bar with contact info */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              {contactInfo?.whatsapp_number && (
                <a 
                  href={`https://wa.me/${formatPhoneForWhatsApp(contactInfo.whatsapp_number)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                  data-testid="header-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="hidden sm:inline">WhatsApp</span>
                </a>
              )}
              {contactInfo?.business_phone && (
                <a 
                  href={`tel:${contactInfo.business_phone.replace(/\D/g, '')}`}
                  className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                  data-testid="header-phone"
                >
                  <Phone className="w-4 h-4" />
                  <span className="hidden sm:inline">{formatPhoneDisplay(contactInfo.business_phone)}</span>
                </a>
              )}
              {contactInfo?.email && (
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors"
                  data-testid="header-email"
                >
                  <Mail className="w-4 h-4" />
                  <span className="hidden sm:inline">{contactInfo.email}</span>
                </a>
              )}
            </div>
            <div className="flex items-center gap-2 text-white text-sm">
              <MapPin className="w-4 h-4 text-slate-400" />
              {contactInfo?.address && <span className="hidden md:inline text-slate-400" data-testid="topbar-address">{contactInfo.address}</span>}
            </div>
          </div>
          
          {/* Logo row with navigation */}
          <div className="flex items-center justify-between">
            {/* Logo - always left aligned on landing page */}
            {businessLogo ? (
              <img 
                src={`${API_URL}${businessLogo}`} 
                alt={contactInfo?.business_name || tenantDisplayName} 
                className={`${logoSizeClasses[logoSize]?.header || 'h-14'} w-auto`}
              />
            ) : (
              <div className={`${logoSizeClasses[logoSize]?.header || 'h-14'} w-auto`} />
            )}
            
            {/* Desktop Navigation - Always Visible */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
              {menuItems.map((item) => (
                <button
                  key={`${item.label}-${item.link}`}
                  onClick={() => goMenu(item.link)}
                  className={`px-3 py-2 text-sm ${item.highlight ? 'text-amber-400 hover:text-amber-300' : 'text-slate-300 hover:text-white'} hover:bg-white/10 rounded-md transition-colors`}
                  data-testid={`nav-menu-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {item.label}
                </button>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="ml-2 border-white/40 bg-white/5 text-white hover:bg-white/15 hover:text-white rounded-full px-4"
                onClick={() => { setLoginError(''); setShowMobileLoginModal(true); }}
                data-testid="header-signin-btn"
              >
                <LogIn className="w-4 h-4 mr-1.5" />
                Sign In
              </Button>
              <Button
                size="sm"
                className="ml-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4"
                onClick={() => expandAuthBox('trial')}
              >
                Get Started
              </Button>
            </nav>
            
            {/* Sign In button for mobile */}
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden border-white/30 text-white hover:bg-white/10"
              onClick={() => {
                setLoginError('');
                setShowMobileLoginModal(true);
              }}
              data-testid="mobile-signin-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Sticky Navigation Bar */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-slate-900/95 backdrop-blur-md shadow-lg py-2 opacity-100' 
            : 'bg-transparent py-4 pointer-events-none opacity-0'
        }`}
        aria-label="Main navigation"
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`transition-opacity ${isScrolled ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`}
            >
              {businessLogo ? (
                <img 
                  src={`${API_URL}${businessLogo}`} 
                  alt={contactInfo?.business_name || tenantDisplayName} 
                  className={`${logoSizeClasses[logoSize]?.nav || 'h-10'} w-auto`}
                />
              ) : (
                <div className={`${logoSizeClasses[logoSize]?.nav || 'h-10'} w-auto`} />
              )}
            </a>

            {/* Desktop Nav Links */}
            <div className={`hidden md:flex items-center gap-1 ${isScrolled ? 'pointer-events-auto' : ''}`}>
              {menuItems.map((item) => (
                <button
                  key={`${item.label}-${item.link}`}
                  onClick={() => goMenu(item.link)}
                  className={`px-3 py-2 text-sm ${item.highlight ? 'text-amber-400 hover:text-amber-300' : 'text-slate-300 hover:text-white'} hover:bg-white/10 rounded-md transition-colors`}
                >
                  {item.label}
                </button>
              ))}
              <Button
                size="sm"
                variant="outline"
                className="ml-2 border-white/40 bg-white/5 text-white hover:bg-white/15 hover:text-white rounded-full px-4"
                onClick={() => { setLoginError(''); setShowMobileLoginModal(true); }}
                data-testid="sticky-nav-signin-btn"
              >
                <LogIn className="w-4 h-4 mr-1.5" />
                Sign In
              </Button>
              <Button
                size="sm"
                className="ml-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-4"
                onClick={() => expandAuthBox('trial')}
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`md:hidden p-2 text-white ${isScrolled ? 'pointer-events-auto' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && isScrolled && (
            <div className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4">
              <div className="flex flex-col gap-2">
                {menuItems.map((item) => (
                  <button
                    key={`${item.label}-${item.link}`}
                    onClick={() => { goMenu(item.link); setMobileMenuOpen(false); }}
                    className={`px-4 py-2 text-left ${item.highlight ? 'text-amber-400 hover:text-amber-300' : 'text-slate-300 hover:text-white'} hover:bg-white/10 rounded-md transition-colors`}
                  >
                    {item.label}
                  </button>
                ))}
                <Button
                  size="sm"
                  className="mt-2 bg-emerald-500 hover:bg-emerald-600 text-white"
                  onClick={() => { expandAuthBox('trial'); setMobileMenuOpen(false); }}
                >
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Banner Announcements */}
      {bannerAnnouncements.length > 0 && (
        <div 
          className="relative z-20"
          style={{ 
            backgroundColor: bannerAnnouncements[currentBannerIndex]?.background_color,
            color: bannerAnnouncements[currentBannerIndex]?.text_color 
          }}
        >
          <div className="max-w-7xl mx-auto px-4 py-2.5">
            <div className="flex items-center justify-center gap-3 text-center">
              {bannerAnnouncements.length > 1 && (
                <button
                  onClick={() => setCurrentBannerIndex(prev => (prev - 1 + bannerAnnouncements.length) % bannerAnnouncements.length)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                  aria-label="Previous announcement"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              
              <div className="flex items-center gap-2 flex-wrap justify-center">
                <span className="font-semibold">{bannerAnnouncements[currentBannerIndex]?.title}</span>
                <span className="hidden sm:inline">—</span>
                <span className="text-sm sm:text-base">{bannerAnnouncements[currentBannerIndex]?.message}</span>
                {bannerAnnouncements[currentBannerIndex]?.link_text && bannerAnnouncements[currentBannerIndex]?.link_url && (
                  <a
                    href={bannerAnnouncements[currentBannerIndex].link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 px-3 py-1 bg-white/20 rounded hover:bg-white/30 text-sm font-medium transition-colors inline-flex items-center gap-1"
                  >
                    {bannerAnnouncements[currentBannerIndex].link_text}
                  </a>
                )}
              </div>
              
              {bannerAnnouncements.length > 1 && (
                <button
                  onClick={() => setCurrentBannerIndex(prev => (prev + 1) % bannerAnnouncements.length)}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                  aria-label="Next announcement"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Dots indicator for multiple banners */}
            {bannerAnnouncements.length > 1 && (
              <div className="flex items-center justify-center gap-1 mt-1.5">
                {bannerAnnouncements.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentBannerIndex(index)}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      index === currentBannerIndex ? 'bg-white w-4' : 'bg-white/50 hover:bg-white/70'
                    }`}
                    aria-label={`Go to announcement ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="hero" className="relative z-10 py-8 lg:py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className={`grid gap-8 lg:gap-12 items-start ${heroPanel ? 'lg:grid-cols-2' : 'max-w-3xl mx-auto text-center'}`} data-testid="hero-grid">
            {/* Left - Hero Content */}
            <div className="text-white space-y-6">
              {/* Academy Branding - Big Bold Montserrat */}
              {contactInfo?.business_name && (
                <div className="mb-2">
                  <h2 
                    className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight text-white uppercase leading-none"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {contactInfo.business_name.split(' ').slice(0, 2).join(' ')}
                  </h2>
                  <h3 
                    className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold tracking-wide uppercase leading-tight"
                    style={{ 
                      fontFamily: "'Montserrat', sans-serif",
                      backgroundImage: 'linear-gradient(to right, var(--theme-primary), var(--theme-accent))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {contactInfo.business_name.split(' ').slice(2).join(' ')}
                  </h3>
                  {contactInfo?.business_tagline && (
                    <p 
                      className="mt-2 text-sm sm:text-base lg:text-lg text-slate-300 font-medium tracking-wide"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {contactInfo.business_tagline}
                    </p>
                  )}
                </div>
              )}

              <div className="inline-flex items-center gap-2 px-3 py-1 bg-theme-success/20 rounded-full text-theme-success text-sm">
                <Sparkles className="w-4 h-4" />
                {getSectionContent('hero')?.tagline_pill || 'Now Enrolling for 2026'}
              </div>
              
              <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                {getSectionContent('hero')?.headline_prefix || "Unlock Your Child's"}
                <span
                  className="text-transparent bg-clip-text"
                  style={{
                    backgroundImage: `linear-gradient(to right, var(--theme-primary), var(--theme-accent))`,
                  }}
                >
                  {' '}{getSectionContent('hero')?.headline_highlight || 'Cricket Potential'}
                </span>
              </h1>
              
              <p className={`text-lg text-slate-300 max-w-lg ${heroPanel ? '' : 'mx-auto'}`}>
                {getSectionContent('hero')?.subheading || 'Professional cricket coaching for all skill levels. Give your child the foundation for excellence.'}
              </p>

              <HeroFeatureCards cards={heroCards} centered={!heroPanel} />
              {!heroPanel && <div className="pt-2"><HeroCta content={heroContent} onAction={heroAction} /></div>}
            </div>

            {heroPanel && (
            <div className="w-full space-y-6">
              {/* Hero Image Gallery */}
              {heroImages.length > 0 ? (
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <div className="aspect-[4/3] relative bg-slate-800">
                    <img
                      src={heroImages[currentHeroImage]?.file_url}
                      alt={heroImages[currentHeroImage]?.title || 'Cricket Academy'}
                      className="w-full h-full object-cover transition-opacity duration-500"
                    />
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    
                    {/* Image caption */}
                    {heroImages[currentHeroImage]?.title && (
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-lg font-semibold text-white drop-shadow-lg">
                          {heroImages[currentHeroImage].title}
                        </h3>
                        {heroImages[currentHeroImage].description && (
                          <p className="text-sm text-slate-200 mt-1 drop-shadow">
                            {heroImages[currentHeroImage].description}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Navigation arrows */}
                    {heroImages.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentHeroImage((prev) => prev === 0 ? heroImages.length - 1 : prev - 1)}
                          className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                          aria-label="Previous image"
                        >
                          <ChevronDown className="w-5 h-5 rotate-90" />
                        </button>
                        <button
                          onClick={() => setCurrentHeroImage((prev) => (prev + 1) % heroImages.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                          aria-label="Next image"
                        >
                          <ChevronDown className="w-5 h-5 -rotate-90" />
                        </button>
                      </>
                    )}

                    {/* Dots indicator */}
                    {heroImages.length > 1 && (
                      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2">
                        {heroImages.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentHeroImage(idx)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              idx === currentHeroImage ? 'bg-emerald-400 w-6' : 'bg-white/50 hover:bg-white/80'
                            }`}
                            aria-label={`Go to image ${idx + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-slate-800 to-slate-900 aspect-[4/3] flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Trophy className="w-10 h-10 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">Latest from the Academy</h3>
                    <p className="text-slate-400 text-sm">Training sessions, tournaments, and player achievements</p>
                  </div>
                </div>
              )}

              <HeroCta content={heroContent} onAction={heroAction} />
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Auth Modal Dialog */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-center text-base sm:text-lg">
              {activeTab === 'login' && 'Sign In to Your Account'}
              {activeTab === 'trial' && 'Request a Free Trial Class'}
              {activeTab === 'register' && 'Create Member Account'}
              {activeTab === 'player' && 'Player Registration'}
            </DialogTitle>
            <DialogDescription className="text-center">
              {activeTab === 'login' && 'Welcome back! Enter your credentials to continue.'}
              {activeTab === 'trial' && 'Start your cricket journey with a free trial class!'}
              {activeTab === 'register' && 'Enroll your child in programs or book lanes for practice.'}
              {activeTab === 'player' && 'Register as a player or claim your existing account.'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Tab Selector */}
          <div className="flex justify-center gap-1 py-2 bg-slate-50 rounded-lg mb-4">
            <button
              onClick={() => setActiveTab('trial')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'trial' ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Trial
            </button>
            <button
              onClick={() => setActiveTab('player')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'player' ? 'bg-purple-500 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Player
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'register' ? 'bg-amber-500 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
              data-testid="tab-members-btn"
            >
              Members
            </button>
            <button
              onClick={() => setActiveTab('login')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                activeTab === 'login' ? 'bg-blue-500 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Sign In
            </button>
          </div>
                  {/* Trial Form */}
                  {activeTab === 'trial' && (
                    <form onSubmit={handleTrialSubmit} className="space-y-4" data-testid="trial-form">
                      <div className="text-center mb-4">
                        <p className="text-slate-600 text-sm">Start your cricket journey with a free trial class!</p>
                      </div>
                      
                      {/* Parent Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Parent's Name *</Label>
                          <Input
                            name="parent_name"
                            value={formData.parent_name}
                            onChange={handleChange}
                            placeholder="Your full name"
                            required
                            className="h-9"
                            data-testid="parent-name-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Email *</Label>
                          <Input
                            type="email"
                            name="parent_email"
                            value={formData.parent_email}
                            onChange={handleChange}
                            placeholder="you@example.com"
                            required
                            className="h-9"
                            data-testid="parent-email-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Phone *</Label>
                          <Input
                            type="tel"
                            name="parent_phone"
                            value={formData.parent_phone}
                            onChange={handleChange}
                            placeholder="(123) 456-7890"
                            required
                            className="h-9"
                            data-testid="parent-phone-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Child's Name *</Label>
                          <Input
                            name="child_name"
                            value={formData.child_name}
                            onChange={handleChange}
                            placeholder="Child's full name"
                            required
                            className="h-9"
                            data-testid="child-name-input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Date of Birth *</Label>
                          <Input
                            type="date"
                            name="date_of_birth"
                            value={formData.date_of_birth}
                            onChange={handleChange}
                            required
                            className="h-9"
                            data-testid="dob-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Skill Level</Label>
                          <Select
                            value={formData.skill_level}
                            onValueChange={(value) => setFormData(prev => ({ ...prev, skill_level: value }))}
                          >
                            <SelectTrigger className="h-9" data-testid="skill-level-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SKILL_LEVELS.map(level => (
                                <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Preferred Days */}
                      <div className="space-y-2">
                        <Label className="text-xs">Preferred Days (select up to 3)</Label>
                        <div className="flex flex-wrap gap-2">
                          {DAYS_OPTIONS.map(day => (
                            <button
                              key={day.value}
                              type="button"
                              onClick={() => toggleDay(day.value)}
                              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                formData.preferred_days.includes(day.value)
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {day.label.slice(0, 3)}
                            </button>
                          ))}
                        </div>
                        {formData.preferred_days.length === 3 && (
                          <p className="text-xs text-amber-600">Maximum 3 days selected</p>
                        )}
                      </div>

                      {/* Create Account Option */}
                      <div className="flex items-center space-x-2 pt-2">
                        <Checkbox
                          id="create_account"
                          checked={formData.create_account}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, create_account: checked }))}
                          data-testid="create-account-checkbox"
                        />
                        <Label htmlFor="create_account" className="text-sm cursor-pointer">
                          Create an account to track your request
                        </Label>
                      </div>

                      {formData.create_account && (
                        <div className="grid grid-cols-2 gap-3 animate-slideDown">
                          <div className="space-y-1">
                            <Label className="text-xs">Password</Label>
                            <div className="relative">
                              <Input
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Min 6 characters"
                                className="h-9 pr-9"
                                data-testid="trial-password-input"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Confirm Password</Label>
                            <Input
                              type={showPassword ? 'text' : 'password'}
                              name="confirm_password"
                              value={formData.confirm_password}
                              onChange={handleChange}
                              placeholder="Confirm password"
                              className="h-9"
                              data-testid="trial-confirm-password-input"
                            />
                          </div>
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                        data-testid="submit-trial-btn"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 animate-spin" />
                            Submitting...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            Request Trial Class
                          </span>
                        )}
                      </Button>
                    </form>
                  )}

                  {/* Player Registration Form */}
                  {activeTab === 'player' && (
                    <form onSubmit={handlePlayerRegister} className="space-y-4" data-testid="player-form">
                      <div className="text-center mb-4">
                        <p className="text-slate-600 text-sm">
                          {claimMode 
                            ? `Claim your existing player account: ${claimCheckResult?.player_name}`
                            : 'Register as a player to view your enrollments and schedule'}
                        </p>
                      </div>

                      {/* Email with claim check */}
                      <div className="space-y-1">
                        <Label className="text-xs">Email Address *</Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="email"
                              value={playerData.email}
                              onChange={(e) => {
                                setPlayerData({ ...playerData, email: e.target.value });
                                setClaimMode(false);
                                setClaimCheckResult(null);
                                setPlayerError('');
                              }}
                              placeholder="your@email.com"
                              required
                              className={`h-9 pl-10 ${playerError ? 'border-red-300 focus:border-red-500' : ''}`}
                              data-testid="player-email-input"
                            />
                          </div>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={checkPlayerEmail}
                            disabled={playerLoading}
                            className="text-xs"
                            data-testid="check-email-btn"
                          >
                            {playerLoading ? 'Checking...' : 'Check'}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Already registered by a parent? Check if your email exists to claim your account.
                        </p>
                        
                        {/* Player Email Error */}
                        {playerError && (
                          <div className="p-2 bg-red-50 border border-red-200 rounded-md mt-2" data-testid="player-error">
                            <p className="text-xs text-red-600">{playerError}</p>
                          </div>
                        )}
                      </div>

                      {/* Claim mode - 2-step verification flow (CVE-5) */}
                      {claimMode ? (
                        <div className="space-y-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                          {claimVerifyStep !== 'verifying' ? (
                            <>
                              <p className="text-sm text-purple-700 font-medium">
                                Player found! Set a password — we&apos;ll email a 6-digit code to verify you own this inbox.
                              </p>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Password *</Label>
                                  <Input
                                    type="password"
                                    value={playerData.password}
                                    onChange={(e) => setPlayerData({ ...playerData, password: e.target.value })}
                                    placeholder="Min 6 characters"
                                    className="h-9"
                                    data-testid="player-password-input"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Confirm *</Label>
                                  <Input
                                    type="password"
                                    value={playerData.confirm_password}
                                    onChange={(e) => setPlayerData({ ...playerData, confirm_password: e.target.value })}
                                    placeholder="Confirm"
                                    className="h-9"
                                    data-testid="player-confirm-password-input"
                                  />
                                </div>
                              </div>
                              <Button
                                type="submit"
                                disabled={playerLoading}
                                className="w-full bg-purple-600 hover:bg-purple-700"
                                data-testid="claim-account-send-code-btn"
                              >
                                {playerLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  'Send Verification Code'
                                )}
                              </Button>
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-purple-700 font-medium">
                                Enter the 6-digit code we just sent to <span className="font-semibold">{playerData.email}</span>. It expires in 15 minutes.
                              </p>
                              <div className="space-y-1">
                                <Label className="text-xs">Verification Code *</Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={6}
                                  value={claimVerifyCode}
                                  onChange={(e) => setClaimVerifyCode(e.target.value.replace(/[^0-9]/g, ''))}
                                  placeholder="123456"
                                  className="h-10 text-center tracking-widest text-lg"
                                  data-testid="claim-verification-code-input"
                                />
                              </div>
                              <Button
                                type="submit"
                                disabled={playerLoading}
                                className="w-full bg-purple-600 hover:bg-purple-700"
                                data-testid="claim-account-btn"
                              >
                                {playerLoading ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  'Verify & Claim Account'
                                )}
                              </Button>
                              <button
                                type="button"
                                onClick={() => {
                                  setClaimVerifyStep(null);
                                  setClaimVerifyCode('');
                                }}
                                className="text-xs text-slate-500 hover:text-slate-700 w-full text-center"
                                data-testid="claim-verification-back-btn"
                              >
                                Use a different password
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setClaimMode(false);
                              setClaimCheckResult(null);
                              setClaimVerifyStep(null);
                              setClaimVerifyCode('');
                            }}
                            className="text-xs text-slate-500 hover:text-slate-700 w-full text-center"
                          >
                            Register as new player instead
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* New registration fields */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Full Name *</Label>
                              <Input
                                value={playerData.name}
                                onChange={(e) => setPlayerData({ ...playerData, name: e.target.value })}
                                placeholder="Your name"
                                className="h-9"
                                data-testid="player-name-input"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Phone</Label>
                              <Input
                                value={playerData.phone}
                                onChange={(e) => setPlayerData({ ...playerData, phone: e.target.value })}
                                placeholder="Phone number"
                                className="h-9"
                                data-testid="player-phone-input"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Date of Birth *</Label>
                              <Input
                                type="date"
                                value={playerData.date_of_birth}
                                onChange={handlePlayerDobChange}
                                className="h-9"
                                data-testid="player-dob-input"
                              />
                              {playerAge !== null && (
                                <p className={`text-xs ${playerAge >= 18 ? 'text-green-600' : 'text-amber-600'}`}>
                                  Age: {playerAge} {playerAge >= 18 ? '(Adult)' : '(Junior - parent required)'}
                                </p>
                              )}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Skill Level</Label>
                              <Select 
                                value={playerData.skill_level} 
                                onValueChange={(v) => setPlayerData({ ...playerData, skill_level: v })}
                              >
                                <SelectTrigger className="h-9" data-testid="player-skill-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="beginner">Beginner</SelectItem>
                                  <SelectItem value="intermediate">Intermediate</SelectItem>
                                  <SelectItem value="advanced">Advanced</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Parent Info - shown if under 18 */}
                          {playerAge !== null && playerAge < 18 && (
                            <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <p className="text-sm text-amber-700 font-medium flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                Parent/Guardian Information Required
                              </p>
                              <div className="grid grid-cols-1 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Parent Name *</Label>
                                  <Input
                                    value={playerData.parent_name}
                                    onChange={(e) => setPlayerData({ ...playerData, parent_name: e.target.value })}
                                    placeholder="Parent's full name"
                                    className="h-9"
                                    data-testid="player-parent-name-input"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Parent Email *</Label>
                                    <Input
                                      type="email"
                                      value={playerData.parent_email}
                                      onChange={(e) => setPlayerData({ ...playerData, parent_email: e.target.value })}
                                      placeholder="parent@email.com"
                                      className="h-9"
                                      data-testid="player-parent-email-input"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Parent Phone</Label>
                                    <Input
                                      value={playerData.parent_phone}
                                      onChange={(e) => setPlayerData({ ...playerData, parent_phone: e.target.value })}
                                      placeholder="Phone"
                                      className="h-9"
                                      data-testid="player-parent-phone-input"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Password */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Password *</Label>
                              <Input
                                type="password"
                                value={playerData.password}
                                onChange={(e) => setPlayerData({ ...playerData, password: e.target.value })}
                                placeholder="Min 6 characters"
                                className="h-9"
                                data-testid="player-password-input"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Confirm Password *</Label>
                              <Input
                                type="password"
                                value={playerData.confirm_password}
                                onChange={(e) => setPlayerData({ ...playerData, confirm_password: e.target.value })}
                                placeholder="Confirm"
                                className="h-9"
                                data-testid="player-confirm-password-input"
                              />
                            </div>
                          </div>

                          <Button
                            type="submit"
                            disabled={playerLoading}
                            className="w-full h-11 bg-purple-600 hover:bg-purple-700"
                            data-testid="player-register-btn"
                          >
                            {playerLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <span className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Register as Player
                              </span>
                            )}
                          </Button>
                        </>
                      )}
                    </form>
                  )}

                  {/* Login Form */}
                  {activeTab === 'login' && (
                    <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                      <div className="text-center mb-4">
                        <p className="text-slate-600 text-sm">Welcome back! Sign in to your account.</p>
                      </div>

                      {/* Maintenance Mode Alert */}
                      {maintenanceMode && (
                        <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="maintenance-alert">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-amber-800">Under Maintenance</p>
                              <p className="text-sm text-amber-700 mt-1">{maintenanceMessage}</p>
                              <p className="text-xs text-amber-600 mt-2">Administrators can still log in.</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Email Address</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="email"
                              value={loginData.email}
                              onChange={(e) => { setLoginError(''); setLoginData({ ...loginData, email: e.target.value }); }}
                              placeholder="you@example.com"
                              required
                              className="h-11 pl-10"
                              data-testid="login-email-inline"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Password</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type={showLoginPassword ? 'text' : 'password'}
                              value={loginData.password}
                              onChange={(e) => { setLoginError(''); setLoginData({ ...loginData, password: e.target.value }); }}
                              placeholder="Enter your password"
                              required
                              className="h-11 pl-10 pr-10"
                              data-testid="login-password-inline"
                            />
                            <button
                              type="button"
                              onClick={() => setShowLoginPassword(!showLoginPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                            >
                              {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          <div className="text-right">
                            <button
                              type="button"
                              onClick={() => {
                                setForgotPasswordEmail(loginData.email);
                                setForgotPasswordSent(false);
                                setShowForgotPassword(true);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                              data-testid="forgot-password-link"
                            >
                              Forgot your password?
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="rememberMeDesktop"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          data-testid="remember-me-checkbox"
                        />
                        <label htmlFor="rememberMeDesktop" className="text-sm text-slate-600 cursor-pointer select-none">
                          Remember me
                        </label>
                      </div>

                      {/* Inline Error Message */}
                      {loginError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="login-error">
                          <p className="text-sm text-red-600 font-medium">{loginError}</p>
                          <p className="text-xs text-red-500 mt-1">
                            If you forgot your password, click "Forgot your password?" above.
                          </p>
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={loginLoading}
                        className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:bg-slate-400"
                        data-testid="login-submit-inline"
                      >
                        {loginLoading ? (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 animate-spin" />
                            Signing In...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <LogIn className="w-4 h-4" />
                            Sign In
                          </span>
                        )}
                      </Button>

                      <div className="text-center pt-4 border-t border-slate-100">
                        <p className="text-sm text-slate-600">
                          New to {tenantDisplayName}?{' '}
                          <button
                            type="button"
                            onClick={() => setActiveTab('register')}
                            className="text-amber-600 hover:text-amber-700 font-medium"
                          >
                            Create an account
                          </button>
                        </p>
                      </div>
                    </form>
                  )}

                  {/* Register Form */}
                  {activeTab === 'register' && (
                    <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                      <div className="text-center mb-4">
                        <p className="text-slate-600 text-sm">Create an account to get started!</p>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label className="text-xs">I want to</Label>
                          <RadioGroup
                            value={registerData.role}
                            onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                            className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                            data-testid="register-role-radio"
                          >
                            <label
                              htmlFor="role-parent"
                              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                                registerData.role === 'parent'
                                  ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500'
                                  : 'border-slate-200 hover:border-slate-300 bg-white'
                              }`}
                              data-testid="role-option-parent"
                            >
                              <RadioGroupItem
                                value="parent"
                                id="role-parent"
                                className="mt-1"
                                data-testid="role-parent-radio"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Users className="w-4 h-4 text-amber-600" />
                                  <span className="text-sm font-medium text-slate-900">
                                    Enroll my child
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                  Manage children, programs &amp; tournaments
                                </p>
                              </div>
                            </label>
                            <label
                              htmlFor="role-customer"
                              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                                registerData.role === 'customer'
                                  ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                  : 'border-slate-200 hover:border-slate-300 bg-white'
                              }`}
                              data-testid="role-option-customer"
                            >
                              <RadioGroupItem
                                value="customer"
                                id="role-customer"
                                className="mt-1"
                                data-testid="role-customer-radio"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Target className="w-4 h-4 text-blue-600" />
                                  <span className="text-sm font-medium text-slate-900">
                                    Book lanes for practice
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                  Reserve practice lanes (no child needed)
                                </p>
                              </div>
                            </label>
                          </RadioGroup>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Full Name *</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="text"
                              value={registerData.name}
                              onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                              placeholder="Your full name"
                              required
                              className="h-11 pl-10"
                              data-testid="register-name-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Email Address *</Label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="email"
                              value={registerData.email}
                              onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                              placeholder="you@example.com"
                              required
                              className="h-11 pl-10"
                              data-testid="register-email-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Phone Number</Label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="tel"
                              value={registerData.phone}
                              onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                              placeholder="(123) 456-7890"
                              className="h-11 pl-10"
                              data-testid="register-phone-input"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Password *</Label>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <Input
                                type={showRegisterPassword ? 'text' : 'password'}
                                value={registerData.password}
                                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                                placeholder="Min 6 characters"
                                required
                                className="h-11 pl-10 pr-10"
                                data-testid="register-password-input"
                              />
                              <button
                                type="button"
                                onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Confirm Password *</Label>
                            <Input
                              type={showRegisterPassword ? 'text' : 'password'}
                              value={registerData.confirm_password}
                              onChange={(e) => setRegisterData({ ...registerData, confirm_password: e.target.value })}
                              placeholder="Confirm password"
                              required
                              className="h-11"
                              data-testid="register-confirm-password-input"
                            />
                          </div>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={registerLoading}
                        className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white font-medium"
                        data-testid="register-submit-btn"
                      >
                        {registerLoading ? (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4 animate-spin" />
                            Creating Account...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <UserPlus className="w-4 h-4" />
                            Create Account
                          </span>
                        )}
                      </Button>

                      <div className="text-center pt-4 border-t border-slate-100">
                        <p className="text-sm text-slate-600">
                          Already have an account?{' '}
                          <button
                            type="button"
                            onClick={() => setActiveTab('login')}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Sign in
                          </button>
                        </p>
                      </div>
                    </form>
                  )}
        </DialogContent>
      </Dialog>

      {/* Mission Statement Section */}
      {sectionEnabled('mission') && (() => {
        const sec = getSectionContent('mission') || {};
        return (
          <section id="programs" className="relative z-10 py-16 px-4 bg-gradient-to-b from-slate-900 to-slate-800">
            <div className="max-w-6xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
                {sec.title || 'Our Mission'}
              </h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto whitespace-pre-line">
                {sec.body || 'We aim to provide a roadmap for all budding players from beginner to professional cricket. Join the fastest growing cricket academy in New Jersey.'}
              </p>
            </div>
          </section>
        );
      })()}

      {/* Coaching Highlights Section */}
      {sectionEnabled('coaching_highlights') && (() => {
        const sec = getSectionContent('coaching_highlights') || {};
        const items = shownItems(sec.items) ?? [
          { icon: 'users', title: 'All Age Groups', desc: 'Programs for U11, U13, U15, U17 & Adults', color: 'blue' },
          { icon: 'target', title: 'Skill Development', desc: 'Batting, Bowling & Fielding Drills', color: 'emerald' },
          { icon: 'trophy', title: 'Competition Ready', desc: 'League & Tournament Preparation', color: 'amber' },
          { icon: 'star', title: 'Monthly Games', desc: 'Indoor games & skill-based camps', color: 'purple' }
        ];
        const ICONS = { users: Users, target: Target, trophy: Trophy, star: Star };
        return (
          <section className="relative z-10 py-16 px-4 bg-slate-800/50">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                  {sec.title || 'Coaching Highlights'}
                </h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                  {sec.subtitle || 'Comprehensive training programs designed for all age groups and skill levels'}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {items.map((item, idx) => {
                  const Icon = ICONS[item.icon] || Star;
                  return (
                    <Card key={idx} className="bg-slate-700/50 border-slate-600 hover:bg-slate-700 transition-colors">
                      <CardContent className="p-4 lg:p-6 text-center">
                        <div className={`w-14 h-14 mx-auto mb-4 rounded-xl bg-${item.color}-500/20 flex items-center justify-center`}>
                          <Icon className={`w-7 h-7 text-${item.color}-400`} />
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                        <p className="text-sm text-slate-400">{item.desc}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}

      {/* What We Offer Section */}
      {sectionEnabled('what_we_offer') && (() => {
        const sec = getSectionContent('what_we_offer') || {};
        const checklist = shownItems(sec.checklist) ?? [
          { title: 'Personalized Coaching', desc: 'Receive consistent guidance and tailored feedback' },
          { title: 'Holistic Development', desc: 'Mental & physical fitness, game awareness' },
          { title: 'Player Pathway', desc: 'From beginner to competitive cricket opportunities' },
          { title: 'Team Spirit', desc: 'Build lifelong friendships & leadership skills' }
        ];
        const cards = shownItems(sec.cards) ?? [
          { icon: 'trophy', title: 'League Play', desc: 'Local & travel league participation', color: 'blue' },
          { icon: 'users', title: 'Small Groups', desc: 'Focused training attention', color: 'emerald' },
          { icon: 'target', title: 'Elite Camps', desc: 'High-intensity performance', color: 'amber' },
          { icon: 'map-pin', title: 'Our Location', desc: 'Easy to reach', color: 'purple' }
        ];
        const ICONS = { trophy: Trophy, users: Users, target: Target, 'map-pin': MapPin, star: Star };
        const COLORS = {
          blue: 'from-blue-600 to-blue-700 text-blue-100',
          emerald: 'from-emerald-600 to-emerald-700 text-emerald-100',
          amber: 'from-amber-600 to-amber-700 text-amber-100',
          purple: 'from-purple-600 to-purple-700 text-purple-100',
        };
        return (
        <section id="coaching" className="relative z-10 py-16 px-4 bg-slate-900">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
                  {sec.title_prefix || 'Train with'} <span className="text-emerald-400">{sec.title_highlight || 'Professional Coaches'}</span>
                </h2>
                <p className="text-slate-300 mb-8">
                  {sec.body || 'Learn from international-level coaches and players who bring experience, passion, and insight to every session.'}
                </p>

                <div className="space-y-4">
                  {checklist.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{item.title}</h4>
                        <p className="text-sm text-slate-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {cards.map((c, idx) => {
                  const Icon = ICONS[c.icon] || Trophy;
                  const cls = COLORS[c.color] || COLORS.blue;
                  const [grad, txt] = [cls.split(' ').slice(0, 2).join(' '), cls.split(' ').slice(2).join(' ')];
                  return (
                    <Card key={idx} className={`bg-gradient-to-br ${grad} border-0 p-6`}>
                      <CardContent className="p-0 text-white">
                        <Icon className="w-10 h-10 mb-4 opacity-80" />
                        <h3 className="text-2xl font-bold mb-1">{c.title}</h3>
                        <p className={`${txt} text-sm`}>{c.desc}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
        );
      })()}

      {/* Player Pathway Section */}
      {sectionEnabled('player_pathway') && (() => {
        const sec = getSectionContent('player_pathway') || {};
        const COLORS = {
          emerald: { stepBg: 'bg-emerald-500', badgeBg: 'bg-emerald-500/20', badgeText: 'text-emerald-400', focusText: 'text-emerald-400', dotBg: 'bg-emerald-500', hoverBorder: 'hover:border-emerald-500/50' },
          teal: { stepBg: 'bg-teal-500', badgeBg: 'bg-teal-500/20', badgeText: 'text-teal-400', focusText: 'text-teal-400', dotBg: 'bg-teal-500', hoverBorder: 'hover:border-teal-500/50' },
          blue: { stepBg: 'bg-blue-500', badgeBg: 'bg-blue-500/20', badgeText: 'text-blue-400', focusText: 'text-blue-400', dotBg: 'bg-blue-500', hoverBorder: 'hover:border-blue-500/50' },
          indigo: { stepBg: 'bg-indigo-500', badgeBg: 'bg-indigo-500/20', badgeText: 'text-indigo-400', focusText: 'text-indigo-400', dotBg: 'bg-indigo-500', hoverBorder: 'hover:border-indigo-500/50' },
          violet: { stepBg: 'bg-violet-500', badgeBg: 'bg-violet-500/20', badgeText: 'text-violet-400', focusText: 'text-violet-400', dotBg: 'bg-violet-500', hoverBorder: 'hover:border-violet-500/50' },
          purple: { stepBg: 'bg-purple-500', badgeBg: 'bg-purple-500/20', badgeText: 'text-purple-400', focusText: 'text-purple-400', dotBg: 'bg-purple-500', hoverBorder: 'hover:border-purple-500/50' },
        };
        const defaultLevels = [
          { level: 'Beginners', age: 'Ages 5-8', focus: 'Fun & Fundamentals', skills: ['Hand-eye coordination', 'Basic batting stance & bowling action', 'Throwing & catching'], color: 'emerald' },
          { level: 'Adv Beginners', age: 'Ages 7-10', focus: 'Building Blocks', skills: ['Introduction to leatherball', 'Longer sessions', 'Intro to fitness'], color: 'teal' },
          { level: 'U11', age: 'Under 11', focus: 'Technical Foundation', skills: ['Shot selection', 'Bowling variations', 'Game awareness'], color: 'blue' },
          { level: 'U13', age: 'Under 13', focus: 'Skill Refinement', skills: ['Advanced techniques', 'Match situations', 'Team strategies'], color: 'indigo' },
          { level: 'U15', age: 'Under 15', focus: 'Performance Ready', skills: ['Power hitting', 'Pace/spin mastery', 'Mental toughness'], color: 'violet' },
          { level: 'Competitive', age: 'U17 & Adults', focus: 'Elite Training', skills: ['Match preparation', 'Video analysis', 'Tournament play'], color: 'purple' },
        ];
        const levels = shownItems(sec.levels) ?? defaultLevels;
        const ENROLL_ICONS = { calendar: Calendar, repeat: Repeat, star: Star };
        const defaultEnroll = [
          { title: '1x Per Week', desc: 'Perfect for beginners or those with busy schedules', icon: 'calendar', color: 'blue', features: ['Weekly group session', 'Skill progression tracking', 'Monthly assessments'] },
          { title: '2x Per Week', desc: 'Recommended for consistent improvement', icon: 'repeat', color: 'emerald', popular: true, features: ['Two sessions per week', 'Accelerated progress', 'Match-day prep included', 'Priority lane booking'] },
          { title: 'Custom Plan', desc: 'Tailored coaching for specific goals', icon: 'star', color: 'purple', features: ['1-on-1 private sessions', 'Personalized curriculum', 'Video analysis sessions', 'Flexible scheduling'] },
        ];
        const enroll = shownItems(sec.enrollment_options) ?? defaultEnroll;
        return (
        <section id="player-pathway" className="relative z-10 py-16 px-4 bg-gradient-to-b from-slate-800 to-slate-900">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                {sec.title_prefix || 'Your Cricket'} <span className="text-emerald-400">{sec.title_highlight || 'Pathway'}</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                {sec.subtitle || 'A structured progression from first steps to competitive excellence. Every level builds on the last.'}
              </p>
            </div>

            <div className="relative mb-16">
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 transform -translate-y-1/2 rounded-full" />
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-2">
                {levels.map((program, idx) => {
                  const c = COLORS[program.color] || COLORS.blue;
                  return (
                    <div key={idx} className="relative group">
                      <div className={`hidden lg:flex absolute -top-3 left-1/2 transform -translate-x-1/2 w-8 h-8 rounded-full ${c.stepBg} items-center justify-center text-white text-sm font-bold z-10 shadow-lg`}>
                        {idx + 1}
                      </div>
                      <Card className={`h-full bg-slate-800/80 border-slate-700 ${c.hoverBorder} transition-all duration-300 group-hover:transform group-hover:-translate-y-1`}>
                        <CardContent className="p-4 text-center">
                          <div className={`inline-block px-3 py-1 rounded-full ${c.badgeBg} ${c.badgeText} text-xs font-semibold mb-2`}>
                            {program.age}
                          </div>
                          <h3 className="text-lg font-bold text-white mb-1">{program.level}</h3>
                          <p className={`${c.focusText} text-sm font-medium mb-3`}>{program.focus}</p>
                          <ul className="space-y-1">
                            {(program.skills || []).map((skill, i) => (
                              <li key={i} className="text-xs text-slate-400 flex items-center justify-center gap-1">
                                <span className={`w-1 h-1 rounded-full ${c.dotBg}`} />
                                {skill}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>

            <div id="pricing" className="mt-12">
              <h3 className="text-2xl font-heading font-bold text-white text-center mb-8">
                {sec.enrollment_title || 'Flexible Enrollment Options'}
              </h3>

              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {enroll.map((opt, idx) => {
                  const Icon = ENROLL_ICONS[opt.icon] || Calendar;
                  const colorTxt = `text-${opt.color}-400`;
                  return (
                    <Card key={idx} className={`bg-slate-800/50 border-slate-700 hover:border-${opt.color}-500/50 transition-colors ${opt.popular ? `bg-gradient-to-b from-${opt.color}-900/50 to-slate-800/50 border-${opt.color}-500/30 relative overflow-hidden` : ''}`}>
                      {opt.popular && (
                        <div className={`absolute top-0 right-0 bg-${opt.color}-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg`}>POPULAR</div>
                      )}
                      <CardContent className="p-4 lg:p-6 text-center">
                        <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-${opt.color}-500/20 flex items-center justify-center`}>
                          <Icon className={`w-8 h-8 ${colorTxt}`} />
                        </div>
                        <h4 className="text-xl font-bold text-white mb-2">{opt.title}</h4>
                        <p className="text-slate-400 text-sm mb-4">{opt.desc}</p>
                        <ul className="text-left space-y-2">
                          {(opt.features || []).map((f, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                              <CheckCircle2 className={`w-4 h-4 ${colorTxt} flex-shrink-0`} />
                              {f}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
        );
      })()}

      {/* Meet Our Coaches Section */}
      {sectionEnabled('our_coaches') && (() => {
        const sec = getSectionContent('our_coaches') || {};
        const people = shownItems(sec.people) ?? [
          { name: 'Monank Patel', role: 'Mentor and Friend', desc: 'Current USA ODI/T20 Captain', highlight: 'Highest run scorer MLC 2025' },
          { name: 'Tapash Baishya', role: 'Head Coach', desc: 'Bangladesh, International', highlight: 'Senior NJ Blackcaps member' },
          { name: 'Coach Benjie', role: 'Beginner Programs', desc: 'ECB Level 2 certified', highlight: 'Manages 5-day/week programs' },
          { name: 'Sohrawordi Shuvo', role: 'U11/U13 Coach', desc: 'Bangladesh, International', highlight: 'Middle order & left arm spin' }
        ];
        return (
        <section id="our-coaches" className="relative z-10 py-16 px-4 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              {sec.title || 'Our Esteemed Coaches'}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {sec.subtitle || 'Learn from certified coaches and international players who are passionate about developing the next generation of cricketers'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {people.map((coach, idx) => (
              <Card key={idx} className="bg-slate-700/50 border-slate-600 overflow-hidden group hover:border-emerald-500/50 transition-colors">
                <CardContent className="p-4 lg:p-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 mx-auto mb-4 flex items-center justify-center text-white text-xl font-bold">
                    {coach.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <h3 className="text-lg font-semibold text-white text-center mb-1">{coach.name}</h3>
                  <p className="text-emerald-400 text-sm text-center mb-3">{coach.role}</p>
                  <p className="text-slate-400 text-sm text-center mb-2">{coach.desc}</p>
                  <p className="text-xs text-slate-500 text-center italic">{coach.highlight}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-400 mb-4">{sec.footer_note || '...and specific coaches for developing specific skill sets'}</p>
          </div>
        </div>
      </section>
        );
      })()}

      {/* Member Benefits Section */}
      {sectionEnabled('member_benefits') && (() => {
        const sec = getSectionContent('member_benefits') || {};
        const items = shownItems(sec.items) ?? [
          { emoji: '🏏', title: 'Discounted Lanes', desc: 'Heavily discounted lane rentals for personal practice' },
          { emoji: '🛒', title: 'Merchandise Discounts', desc: 'Exclusive discounts on all cricket merchandise' },
          { emoji: '🎉', title: 'Party Room Access', desc: 'Celebrate birthdays with your cricket family' },
          { emoji: '📅', title: 'Year-Round Coaching', desc: 'We never stop playing - train all year' }
        ];
        return (
        <section className="relative z-10 py-16 px-4 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
                {sec.title || 'Additional Member Benefits'}
              </h2>
              {sec.subtitle && <p className="text-slate-400 max-w-2xl mx-auto">{sec.subtitle}</p>}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {items.map((benefit, idx) => (
                <div key={idx} className="bg-slate-700/30 rounded-xl p-6 text-center hover:bg-slate-700/50 transition-colors">
                  <div className="text-4xl mb-4">{benefit.emoji || benefit.icon}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-400">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        );
      })()}

      {/* CTA Section */}
      {sectionEnabled('cta') && (() => {
        const sec = getSectionContent('cta') || {};
        return (
        <section id="contact" className="relative z-10 py-16 px-4 bg-gradient-to-r from-emerald-600 to-blue-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              {sec.title || 'Ready to Start Your Cricket Journey?'}
            </h2>
            <p className="text-lg text-white/90 mb-8">
              {sec.body || 'New batches starting now. Reserve your spot today and become part of a premier cricket academy.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-emerald-600 hover:bg-white/90 font-semibold px-8"
                onClick={() => {
                  setActiveTab('trial');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                {sec.button_primary_text || 'Request a Trial Class'}
              </Button>
              {contactInfo?.whatsapp_number && (
                <a
                  href={`https://wa.me/${formatPhoneForWhatsApp(contactInfo.whatsapp_number)}?text=cricket%20coaching%20enquiry`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {sec.button_secondary_text || 'WhatsApp Us'}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </section>
        );
      })()}

      {/* Footer */}
      {sectionEnabled('footer') && (() => {
        const sec = getSectionContent('footer') || {};
        return (
        <footer className="relative z-10 py-6 px-4 border-t border-white/10 mt-8">
          <div className="max-w-6xl mx-auto">
            {sec.show_social !== false && (
              <div className="flex flex-wrap items-center justify-center gap-6 mb-4">
                {contactInfo?.whatsapp_number && (
                  <a
                    href={`https://wa.me/${formatPhoneForWhatsApp(contactInfo.whatsapp_number)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors text-sm"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>WhatsApp Us</span>
                  </a>
                )}
                {contactInfo?.business_phone && (
                  <a
                    href={`tel:${contactInfo.business_phone.replace(/\D/g, '')}`}
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors text-sm"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{formatPhoneDisplay(contactInfo.business_phone)}</span>
                  </a>
                )}
                {contactInfo?.email && (
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors text-sm"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{contactInfo.email}</span>
                  </a>
                )}
              </div>
            )}

            {sec.show_legal_links !== false && (
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
            <a href="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms-of-service" className="text-slate-400 hover:text-white transition-colors">Terms of Service</a>
            <a href="/shipping-policy" className="text-slate-400 hover:text-white transition-colors">Shipping Policy</a>
            <a href="/return-refund-policy" className="text-slate-400 hover:text-white transition-colors">Returns & Refunds</a>
            <a href="/do-not-sell-info" className="text-slate-400 hover:text-white transition-colors">Do Not Sell My Info</a>
              </div>
            )}

            <div className="text-center text-slate-400 text-sm">
              <p>{sec.copyright_text || `© 2026 ${tenantDisplayName}. All rights reserved.`}</p>
              {contactInfo?.address && (
                <p className="mt-1 flex items-center justify-center gap-1" data-testid="footer-address">
                  <MapPin className="w-4 h-4" />
                  {contactInfo.address}
                </p>
              )}
            </div>
          </div>
        </footer>
        );
      })()}

      {/* Mobile Login Modal */}
      <Dialog open={showMobileLoginModal} onOpenChange={setShowMobileLoginModal}>
        <DialogContent className="sm:max-w-md" data-testid="mobile-login-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5 text-blue-600" />
              Sign In
            </DialogTitle>
            <DialogDescription>
              Welcome back! Sign in to your account.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleLogin} className="space-y-4" data-testid="mobile-login-form">
            {/* Maintenance Mode Alert */}
            {maintenanceMode && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="modal-maintenance-alert">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Under Maintenance</p>
                    <p className="text-xs text-amber-700">{maintenanceMessage}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-sm">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => { setLoginError(''); setLoginData({ ...loginData, email: e.target.value }); }}
                    placeholder="you@example.com"
                    required
                    className="h-11 pl-10"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showLoginPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => { setLoginError(''); setLoginData({ ...loginData, password: e.target.value }); }}
                    placeholder="Enter your password"
                    required
                    className="h-11 pl-10 pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMobileLoginModal(false);
                      setForgotPasswordEmail(loginData.email);
                      setForgotPasswordSent(false);
                      setShowForgotPassword(true);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                    data-testid="modal-forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="rememberMeMobile"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                data-testid="mobile-remember-me-checkbox"
              />
              <label htmlFor="rememberMeMobile" className="text-sm text-slate-600 cursor-pointer select-none">
                Remember me
              </label>
            </div>

            {/* Inline Error Message */}
            {loginError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="modal-login-error">
                <p className="text-sm text-red-600 font-medium">{loginError}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loginLoading}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:bg-slate-400"
              data-testid="login-submit"
            >
              {loginLoading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing In...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-4 h-4" />
                  Sign In
                </span>
              )}
            </Button>

            <div className="text-center pt-3 border-t border-slate-100">
              <p className="text-sm text-slate-600">
                New to {tenantDisplayName}?{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShowMobileLoginModal(false);
                    setActiveTab('register');
                    const authSection = document.getElementById('auth-section');
                    if (authSection) {
                      authSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="text-amber-600 hover:text-amber-700 font-medium"
                >
                  Create an account
                </button>
              </p>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-blue-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {forgotPasswordSent 
                ? "Check your email for password reset instructions."
                : "Enter your email address and we'll send you a link to reset your password."}
            </DialogDescription>
          </DialogHeader>

          {forgotPasswordSent ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center p-4 bg-emerald-50 rounded-lg">
                <div className="text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-2" />
                  <p className="text-emerald-700 font-medium">Email Sent!</p>
                  <p className="text-sm text-emerald-600 mt-1">
                    If an account exists for <span className="font-mono">{forgotPasswordEmail}</span>, 
                    you will receive a password reset link shortly.
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-500 text-center">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setForgotPasswordSent(false);
                    setForgotPasswordEmail('');
                  }}
                  data-testid="try-again-btn"
                >
                  Try Again
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordSent(false);
                  }}
                  data-testid="back-to-login-btn"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="forgotEmail">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="forgotEmail"
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-10"
                    data-testid="forgot-password-email-input"
                    autoFocus
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={forgotPasswordLoading}
                data-testid="forgot-password-submit-btn"
              >
                {forgotPasswordLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Reset Link
                  </>
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  Back to Login
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
;
